import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  StorefrontIcon,
} from '@phosphor-icons/react/dist/ssr';

import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { useT } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';
import { MARKET_CATEGORIES } from '../lib/market-categories';
import { tryGetTelegram } from '../telegram';

export default function Markets() {
  const t = useT();
  const navigate = useNavigate();
  useTelegramBackButton();
  useNavStackEntry({ label: t('market.breadcrumb') });

  const [q, setQ] = useState('');

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    tryGetTelegram()?.HapticFeedback?.impactOccurred?.('light');
    navigate(`/markets/search/${encodeURIComponent(term)}`);
  }

  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          <StorefrontIcon weight="fill" className="size-3" />
          {t('market.eyebrow')}
        </span>
      }
      title={t('market.title')}
      description={t('market.description')}
    >
      <NavBar />

      <Card>
        <CardHeader>
          <CardEyebrow>
            <MagnifyingGlassIcon weight="fill" className="size-3" />
            {t('market.search_label')}
          </CardEyebrow>
        </CardHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('market.search_placeholder')}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="search"
            className="flex-1"
          />
          <Button
            type="submit"
            size="lg"
            disabled={!q.trim()}
            className="sm:w-auto"
          >
            <MagnifyingGlassIcon weight="bold" className="size-4" />
            {t('market.search_cta')}
          </Button>
        </form>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg">
            {t('market.categories_title')}
          </CardTitle>
          <CardDescription>
            {t('market.categories_description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {MARKET_CATEGORIES.map((cat) => (
              <Link
                key={cat.key}
                to={`/markets/cat/${cat.key}`}
                onClick={() =>
                  tryGetTelegram()?.HapticFeedback?.impactOccurred?.('light')
                }
                className={cn(
                  'group bg-card/85 supports-backdrop-filter:bg-card/80 ring-foreground/10',
                  'hover:ring-primary/30 hover:bg-card hover:-translate-y-0.5',
                  'relative flex flex-col items-center gap-2 overflow-hidden rounded-2xl px-3 py-4',
                  'ring-1 backdrop-blur-md transition-all',
                )}
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-primary/40 to-transparent opacity-0 transition-opacity group-hover:opacity-100"
                />
                <span
                  aria-hidden
                  className="text-2xl leading-none sm:text-3xl"
                >
                  {cat.emoji}
                </span>
                <span className="text-foreground text-center text-[13px] font-semibold tracking-tight sm:text-sm">
                  {t(`market.cat.${cat.key}`)}
                </span>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </ScreenShell>
  );
}
